import React, { useEffect, useRef, useState } from 'react';
import { Upload, Tooltip, Button, theme } from 'antd';
import { message } from '../services/toastMessage';
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload';
import { PlusOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { uploadPhotos, MediaDomain } from '../services/MediaService';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

const DEFAULT_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export interface PhotoUploadProps {
  value?: string[];
  /** Order-matched with value — the small copy for grid/card views, prefilled in edit mode. */
  thumbnails?: string[];
  onChange?: (photos: string[], thumbnails: string[]) => void;
  /** Which Blob Storage folder new uploads land in — see MediaDomain. */
  domain: MediaDomain;
  maxCount?: number;
  maxSizeMB?: number;
  allowedTypes?: string[];
  invalidTypeMessage?: string;
  tooLargeMessage?: string;
  uploadFailedMessage?: string;
  disabled?: boolean;
  /** Tooltip on the star toggle used to promote a photo to index 0 (the "main" one shown on cards). */
  setMainLabel?: string;
  /** Tooltip shown on whichever photo is currently first. */
  mainLabel?: string;
}

/**
 * Ported from Authentication76's PhotoUpload — reusable photo picker: newly
 * picked files are uploaded to Blob Storage via HeavyDutyMachineryApi's
 * MediaController and the resulting URLs are what gets sent onChange. Wire
 * it inside a Form.Item with a `name` and it behaves like any other
 * controlled field via the value/onChange contract.
 */
const PhotoUpload: React.FC<PhotoUploadProps> = ({
  value,
  thumbnails,
  onChange,
  domain,
  maxCount = 5,
  maxSizeMB = 5,
  allowedTypes = DEFAULT_ALLOWED_TYPES,
  invalidTypeMessage,
  tooLargeMessage,
  uploadFailedMessage,
  disabled,
  setMainLabel,
  mainLabel,
}) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const resolvedSetMainLabel = setMainLabel ?? translate(t, TRANSLATIONS.PHOTO_UPLOAD_SET_MAIN_LABEL);
  const resolvedMainLabel = mainLabel ?? translate(t, TRANSLATIONS.PHOTO_UPLOAD_MAIN_LABEL);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const initializedRef = useRef(false);

  // Prefill from an existing `value` (edit mode) once, without clobbering
  // the list once the user starts adding/removing photos of their own.
  useEffect(() => {
    if (initializedRef.current || !value || value.length === 0) return;
    initializedRef.current = true;
    setFileList(
      value.map((url, index) => ({
        uid: `existing-${index}`,
        name: `photo-${index + 1}`,
        status: 'done',
        url,
        thumbUrl: thumbnails?.[index] || url,
      }))
    );
  }, [value, thumbnails]);

  const beforeUpload = (file: RcFile) => {
    if (!allowedTypes.includes(file.type)) {
      message.error(invalidTypeMessage || translate(t, TRANSLATIONS.PHOTO_UPLOAD_INVALID_TYPE));
      return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 >= maxSizeMB) {
      message.error(tooLargeMessage || t(TRANSLATIONS.PHOTO_UPLOAD_TOO_LARGE, { maxSizeMB }));
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  // Newly picked files get uploaded to Blob Storage in one batch; pre-filled
  // ones (edit mode) already carry their real url/thumbUrl.
  const computePhotos = async (list: UploadFile[]): Promise<{ photos: string[]; thumbnails: string[] }> => {
    const newFiles = list.filter((file) => file.originFileObj);
    let uploaded: { url: string; thumbnailUrl: string }[] = [];

    if (newFiles.length > 0) {
      try {
        uploaded = await uploadPhotos(domain, newFiles.map((file) => file.originFileObj as File));
      } catch {
        message.error(uploadFailedMessage || translate(t, TRANSLATIONS.PHOTO_UPLOAD_FAILED));
      }
    }

    let uploadIndex = 0;
    const photos: string[] = [];
    const thumbs: string[] = [];
    for (const file of list) {
      if (file.originFileObj) {
        const result = uploaded[uploadIndex++];
        if (result) {
          photos.push(result.url);
          thumbs.push(result.thumbnailUrl);
        }
      } else if (file.url) {
        photos.push(file.url);
        thumbs.push(file.thumbUrl || file.url);
      }
    }
    return { photos, thumbnails: thumbs };
  };

  const handleChange: UploadProps['onChange'] = async ({ fileList: newFileList }) => {
    const limited = newFileList.slice(0, maxCount);
    setFileList(limited);
    const { photos, thumbnails: thumbs } = await computePhotos(limited);
    onChange?.(photos, thumbs);
  };

  // Promotes a photo to index 0 — that's the one cards show as the thumbnail.
  const handleSetMain = async (uid: string) => {
    const index = fileList.findIndex((file) => file.uid === uid);
    if (index <= 0) return;
    const reordered = [fileList[index], ...fileList.slice(0, index), ...fileList.slice(index + 1)];
    setFileList(reordered);
    const { photos, thumbnails: thumbs } = await computePhotos(reordered);
    onChange?.(photos, thumbs);
  };

  return (
    <Upload
      listType="picture-card"
      fileList={fileList}
      beforeUpload={beforeUpload}
      onChange={handleChange}
      maxCount={maxCount}
      multiple
      accept={allowedTypes.join(',')}
      disabled={disabled}
      itemRender={(originNode, file, currentFileList) => {
        if (currentFileList.length <= 1) return originNode;
        const isMain = currentFileList[0]?.uid === file.uid;
        return (
          <div style={{ position: 'relative' }}>
            {originNode}
            <Tooltip title={isMain ? resolvedMainLabel : resolvedSetMainLabel}>
              <Button
                size="small"
                shape="circle"
                icon={isMain ? <StarFilled style={{ color: token.colorWarning }} /> : <StarOutlined />}
                style={{ position: 'absolute', top: 4, left: 4, zIndex: 10 }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isMain) handleSetMain(file.uid);
                }}
              />
            </Tooltip>
          </div>
        );
      }}
    >
      {fileList.length >= maxCount ? null : (
        <div>
          <PlusOutlined />
        </div>
      )}
    </Upload>
  );
};

export default PhotoUpload;
